import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, Eye, EyeOff, ShieldCheck, ArrowLeft, KeyRound, MessageCircle, Bot, Smartphone, LayoutDashboard, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Social login icons as SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const DiscordIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"/>
  </svg>
);

// Champ de saisie commun (connexion, inscription, codes).
const FIELD = "h-12 rounded-xl border-white/[0.08] bg-white/[0.03] text-[14px] text-white placeholder:text-white/25 transition-all focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-blue-500/60 focus:bg-white/[0.05] focus:shadow-[0_0_0_4px_rgba(59,130,246,0.12)]";

// Colonne de gauche (ordinateur) : marque, promesse et points forts.
const BRAND_COPY = {
  login: { title: "Votre espace, vos produits.", text: "Retrouvez vos bots, vos scripts FiveM, vos licences et vos factures au même endroit." },
  register: { title: "Lancez votre projet en quelques minutes.", text: "Un compte suffit pour acheter, configurer et suivre tous vos produits VantaPulse." },
  "2fa": { title: "Une sécurité de plus.", text: "La vérification en deux étapes protège votre compte et vos achats." },
};

const BRAND_POINTS = [
  { icon: Bot, text: "Bots Discord prêts à l'emploi" },
  { icon: Smartphone, text: "Scripts FiveM avec mises à jour" },
  { icon: LayoutDashboard, text: "Tableau de bord en ligne" },
  { icon: Sparkles, text: "Assistant IA et support intégrés" },
];

function AuthBrandPanel({ mode }) {
  const copy = BRAND_COPY[mode] || BRAND_COPY.login;
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden border-r border-white/[0.06] bg-[#0a0f24] p-9 md:flex">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_0%_0%,rgba(59,130,246,0.38),transparent_55%),radial-gradient(90%_70%_at_100%_100%,rgba(139,92,246,0.35),transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.15] [background-image:linear-gradient(rgba(255,255,255,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:32px_32px] [mask-image:radial-gradient(ellipse_at_center,#000_30%,transparent_75%)]" />
      <div className="auth-orb pointer-events-none absolute -right-16 top-1/3 h-48 w-48 rounded-full bg-gradient-to-br from-blue-500/40 to-violet-600/30 blur-3xl" />

      <div className="relative flex items-center gap-2.5">
        <img src="/images/logo-navbar.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-white/15" />
        <span className="font-heading text-[16px] font-bold tracking-tight">VantaPulse</span>
      </div>

      <div className="relative my-10">
        <h3 key={mode} className="auth-swap font-heading text-[28px] font-bold leading-[1.15] tracking-tight text-white">{copy.title}</h3>
        <p key={`${mode}-t`} className="auth-swap mt-3 text-[14px] leading-relaxed text-white/60" style={{ animationDelay: "60ms" }}>{copy.text}</p>
        <ul className="mt-7 space-y-3">
          {BRAND_POINTS.map(({ icon: Icon, text }, index) => (
            <li key={text} className="auth-swap flex items-center gap-3 text-[13.5px] text-white/80" style={{ animationDelay: `${120 + index * 50}ms` }}>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.07] ring-1 ring-inset ring-white/10">
                <Icon className="h-4 w-4 text-blue-200" />
              </span>
              {text}
            </li>
          ))}
        </ul>
      </div>

      <div className="relative flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur">
        <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-300" />
        <p className="text-[12px] leading-snug text-white/60">Connexion chiffrée, double authentification disponible et paiements sécurisés.</p>
      </div>
    </div>
  );
}

export const AuthModal = ({ isOpen, onClose, mode, setMode }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // Second factor step, shown after a password login that requires 2FA
  const [twoFAState, setTwoFAState] = useState(null); // { methods, pending_token, method }
  const [twoFACode, setTwoFACode] = useState("");

  // Alternative au mot de passe : un code à 6 chiffres envoyé par email ou par MP
  // Discord (voir /auth/login-code sur le backend).
  const [useCode, setUseCode] = useState(false);
  const [codeChannel, setCodeChannel] = useState("email");
  const [codeSent, setCodeSent] = useState(false);
  const [loginCode, setLoginCode] = useState("");

  const { login } = useAuth();

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setUsername("");
    setErrors({});
    setTwoFAState(null);
    setTwoFACode("");
    setUseCode(false);
    setCodeSent(false);
    setLoginCode("");
  };

  const closeModal = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Email invalide";
    }

    if (mode === "login" && !password.trim()) newErrors.password = "Le mot de passe est requis";

    if (mode === "register" && !username.trim()) {
      newErrors.username = "Le nom d'utilisateur est requis";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const finishLogin = (data) => {
    login(data.access_token, data.user);
    toast.success("Connexion réussie !");
    closeModal();
    navigate("/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const referredByCode = localStorage.getItem("vantapulse_ref_code") || undefined;
      const data = mode === "login"
        ? { email, password }
        : { email, username, referred_by_code: referredByCode };

      const response = await axios.post(`${API}${endpoint}`, data);

      if (response.data.requires_2fa) {
        setTwoFAState({
          methods: response.data.methods,
          pending_token: response.data.pending_token,
          method: response.data.methods[0],
        });
        return;
      }

      if (response.data.pending_signup) {
        // Rien n'est encore enregistré : le compte n'est créé qu'une fois le mot
        // de passe validé sur l'onboarding (voir /auth/complete-signup).
        sessionStorage.setItem("pending_signup", JSON.stringify({
          pending_token: response.data.pending_token,
        }));
        resetForm();
        onClose();
        navigate("/onboarding");
        return;
      }

      localStorage.removeItem("vantapulse_ref_code");
      login(response.data.access_token, response.data.user);
      toast.success("Connexion réussie !");
      resetForm();
      onClose();
      navigate("/");
    } catch (error) {
      if (mode === "login" && error.response?.status === 404) {
        toast.info("Aucun compte trouvé avec cet email, créez-en un !");
        setMode("register");
        setPassword("");
        setErrors({});
      } else {
        toast.error(error.response?.data?.detail || "Une erreur s'est produite");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!twoFACode.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/login-verify`, {
        pending_token: twoFAState.pending_token,
        method: twoFAState.method,
        code: twoFACode.trim(),
      });
      finishLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const requestLoginCode = async (channel) => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrors({ email: "Email invalide" });
      return;
    }
    setErrors({});
    setCodeChannel(channel);
    setLoading(true);
    try {
      await axios.post(`${API}/auth/login-code/request`, { channel, email });
      setCodeSent(true);
      toast.success(channel === "email" ? "Code envoyé par email" : "Code envoyé sur Discord");
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info("Aucun compte trouvé avec cet email, créez-en un !");
        setMode("register");
        setUseCode(false);
        setCodeSent(false);
      } else {
        toast.error(error.response?.data?.detail || "Impossible d'envoyer le code");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginCode = async () => {
    if (loginCode.length !== 6) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login-code/verify`, {
        channel: codeChannel,
        code: loginCode,
        email,
      });

      if (response.data.requires_2fa) {
        setTwoFAState({
          methods: response.data.methods,
          pending_token: response.data.pending_token,
          method: response.data.methods[0],
        });
        return;
      }

      if (response.data.access_token) {
        localStorage.removeItem("vantapulse_ref_code");
        finishLogin(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const backToPasswordMode = () => {
    setUseCode(false);
    setCodeSent(false);
    setLoginCode("");
  };

  const handleSocialLogin = (provider) => {
    if (provider === 'Google') {
      // origin : la connexion revient sur ce même site (vanta-pulse.shop ou Vercel)
      window.location.href = `${API}/auth/google?origin=${encodeURIComponent(window.location.origin)}`;
    } else if (provider === 'Discord') {
      // Passe par le backend (qui connaît déjà DISCORD_CLIENT_ID et gère le paramètre
      // state anti-CSRF) plutôt que de reconstruire l'URL Discord ici : ça évite de
      // dépendre d'une variable d'env frontend séparée (REACT_APP_DISCORD_CLIENT_ID)
      // qui peut se désynchroniser de celle du backend et casser la connexion avec
      // une erreur Discord peu explicite ("Corps de formulaire non valide").
      const origin = encodeURIComponent(window.location.origin);
      window.location.href = `${API}/auth/discord?origin=${origin}`;
    } else {
      toast.info(`Connexion ${provider} bientôt disponible`);
    }
  };

  const switchMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setErrors({});
    setUseCode(false);
    setCodeSent(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent
        overlayClassName="bg-black/65 backdrop-blur-md"
        className="auth-modal-content w-[calc(100vw-1.5rem)] max-w-[900px] gap-0 overflow-hidden rounded-[26px] sm:rounded-[26px] border border-white/[0.09] bg-[#0b0d16]/[0.97] p-0 shadow-[0_40px_120px_-30px_rgba(59,130,246,0.45)] backdrop-blur-2xl md:grid md:grid-cols-[0.95fr_1.05fr] [&>button:last-child]:right-5 [&>button:last-child]:top-5 [&>button:last-child]:rounded-lg [&>button:last-child]:p-1.5 [&>button:last-child]:text-white/50 [&>button:last-child]:hover:bg-white/[0.06]"
      >
        <AuthBrandPanel mode={twoFAState ? "2fa" : mode} />
        <div className="relative max-h-[92vh] overflow-y-auto p-6 sm:p-9">
          {twoFAState ? (
            <>
              <div className="text-center mb-6 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-3xl rounded-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-blue-500/25">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <DialogTitle className="font-heading text-2xl font-bold tracking-tighter">
                    Vérification en deux étapes
                  </DialogTitle>
                  <p className="text-text-secondary text-sm mt-2">
                    {twoFAState.method === "totp"
                      ? "Entrez le code affiché dans votre application d'authentification"
                      : "Entrez le code envoyé par email"}
                  </p>
                </div>
              </div>

              <Input
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleVerify2FA()}
                className={`${FIELD} mb-4 h-14 text-center font-mono text-2xl tracking-[0.5em]`}
              />

              <Button
                onClick={handleVerify2FA}
                disabled={loading || twoFACode.length !== 6}
                className="h-12 w-full rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 font-semibold text-white shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)] transition-all hover:brightness-110"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Vérifier"}
              </Button>

              {twoFAState.methods.length > 1 && (
                <div className="flex justify-center gap-3 mt-4">
                  {twoFAState.methods.map((m) => (
                    <button
                      key={m}
                      onClick={() => setTwoFAState((s) => ({ ...s, method: m }))}
                      className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${twoFAState.method === m ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60"}`}
                    >
                      {m === "totp" ? "Authenticator" : "Email"}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => setTwoFAState(null)}
                className="mt-6 text-sm text-white/40 hover:text-white/60 transition-colors flex items-center gap-1.5 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
            </>
          ) : (
            <>
              <div className="mb-6 flex items-center gap-2.5 md:hidden">
                <img src="/images/logo-navbar.png" alt="" className="h-8 w-8 rounded-full ring-1 ring-white/10" />
                <span className="font-heading text-[15px] font-bold tracking-tight">VantaPulse</span>
              </div>
              <DialogHeader className="mb-6 space-y-0 text-left">
                <DialogTitle className="font-heading text-[26px] font-bold tracking-tight">
                  {mode === "login" ? "Content de vous revoir" : "Créer un compte"}
                </DialogTitle>
                <p className="mt-1.5 text-sm text-white/50">
                  {mode === "login"
                    ? "Connectez-vous à votre compte VantaPulse"
                    : "Rejoignez VantaPulse et accédez à vos produits"}
                </p>
              </DialogHeader>

              <div className="relative mb-6 grid grid-cols-2 rounded-xl bg-white/[0.04] p-1 ring-1 ring-inset ring-white/[0.06]" role="tablist">
                <span
                  className="absolute bottom-1 left-1 top-1 w-[calc(50%-4px)] rounded-[9px] bg-gradient-to-b from-white/[0.12] to-white/[0.06] shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_4px_14px_-4px_rgba(0,0,0,0.6)] transition-transform duration-300"
                  style={{ transform: mode === "register" ? "translateX(100%)" : "translateX(0)", transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)" }}
                />
                {[["login", "Connexion"], ["register", "Inscription"]].map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={mode === m}
                    data-testid={`auth-tab-${m}`}
                    onClick={() => { if (mode !== m) switchMode(); }}
                    className={`relative z-10 rounded-[9px] py-2 text-[13px] font-medium transition-colors ${mode === m ? "text-white" : "text-white/45 hover:text-white/75"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Social Login Buttons */}
              <div className="mb-6 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="google-login-btn"
                  onClick={() => handleSocialLogin('Google')}
                  aria-label="Continuer avec Google"
                  className="flex h-11 items-center justify-center gap-2.5 rounded-xl border border-white/[0.09] bg-white/[0.035] text-[13.5px] font-medium text-white transition-all duration-200 hover:-translate-y-px hover:border-white/20 hover:bg-white/[0.07] active:translate-y-0"
                >
                  <GoogleIcon />
                  <span>Google</span>
                </button>

                <button
                  type="button"
                  data-testid="discord-login-btn"
                  onClick={() => handleSocialLogin('Discord')}
                  aria-label="Continuer avec Discord"
                  className="flex h-11 items-center justify-center gap-2.5 rounded-xl border border-[#5865F2]/35 bg-[#5865F2]/[0.14] text-[13.5px] font-medium text-white transition-all duration-200 hover:-translate-y-px hover:border-[#5865F2]/60 hover:bg-[#5865F2]/25 active:translate-y-0"
                >
                  <DiscordIcon />
                  <span>Discord</span>
                </button>
              </div>

              {/* Séparateur */}
              <div className="mb-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.14em] text-white/30">
                <span className="h-px flex-1 bg-gradient-to-r from-transparent to-white/10" />
                ou avec votre e-mail
                <span className="h-px flex-1 bg-gradient-to-l from-transparent to-white/10" />
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <Label htmlFor="username" className="mb-1.5 block text-[13px] font-medium text-white/80">
                      Nom d'utilisateur
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <Input
                        id="username"
                        data-testid="auth-username-input"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Votre pseudo"
                        className={`${FIELD} pl-11 ${errors.username ? 'border-status-error' : ''}`}
                      />
                    </div>
                    {errors.username && (
                      <p className="text-status-error text-xs mt-1">{errors.username}</p>
                    )}
                  </div>
                )}

                {/* Email field */}
                <div>
                  <Label htmlFor="email" className="mb-1.5 block text-[13px] font-medium text-white/80">
                    Adresse e-mail
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                    <Input
                      id="email"
                      data-testid="auth-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      disabled={mode === "login" && useCode && codeSent}
                      className={`${FIELD} pl-11 disabled:opacity-50 ${errors.email ? 'border-status-error' : ''}`}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-status-error text-xs mt-1">{errors.email}</p>
                  )}
                </div>

                {mode === "login" && !useCode && (
                  <div>
                    <Label htmlFor="password" className="mb-1.5 block text-[13px] font-medium text-white/80">
                      Mot de passe
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                      <Input
                        id="password"
                        data-testid="auth-password-input"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className={`${FIELD} pl-11 pr-11 ${errors.password ? 'border-status-error' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-white transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-status-error text-xs mt-1">{errors.password}</p>
                    )}
                    <div className="flex items-center justify-between mt-2">
                      <button
                        type="button"
                        onClick={() => { closeModal(); navigate("/forgot-password"); }}
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium"
                      >
                        Mot de passe oublié ?
                      </button>
                      <button
                        type="button"
                        data-testid="switch-to-code-login"
                        onClick={() => setUseCode(true)}
                        className="text-xs text-white/50 hover:text-white/80 transition-colors flex items-center gap-1"
                      >
                        <KeyRound className="w-3.5 h-3.5" />
                        Se connecter avec un code
                      </button>
                    </div>
                  </div>
                )}

                {mode === "login" && useCode && (
                  <div className="space-y-3">
                    {!codeSent ? (
                      <>
                        <button
                          type="button"
                          data-testid="request-code-email"
                          onClick={() => requestLoginCode("email")}
                          disabled={loading}
                          className="w-full py-3 rounded-xl flex items-center justify-center gap-3 font-medium text-white bg-white/[0.04] border border-white/10 hover:border-blue-400/40 hover:bg-blue-500/10 transition-all disabled:opacity-50"
                        >
                          <Mail className="w-4 h-4" />
                          Recevoir un code par email
                        </button>
                        <button
                          type="button"
                          data-testid="request-code-discord"
                          onClick={() => requestLoginCode("discord")}
                          disabled={loading}
                          className="w-full py-3 rounded-xl flex items-center justify-center gap-3 font-medium text-white bg-gradient-to-r from-[#5865F2]/15 to-[#7289da]/15 border border-[#5865F2]/30 hover:border-[#5865F2]/60 transition-all disabled:opacity-50"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Recevoir un code sur Discord
                        </button>
                      </>
                    ) : (
                      <>
                        <Input
                          value={loginCode}
                          onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          onKeyDown={(e) => e.key === "Enter" && verifyLoginCode()}
                          placeholder="000000"
                          maxLength={6}
                          autoFocus
                          className={`${FIELD} h-14 text-center font-mono text-2xl tracking-[0.5em]`}
                        />
                        <button
                          type="button"
                          onClick={() => requestLoginCode(codeChannel)}
                          disabled={loading}
                          className="text-xs text-blue-400 hover:text-blue-300 transition-colors font-medium disabled:opacity-50"
                        >
                          Renvoyer le code
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={backToPasswordMode}
                      className="text-xs text-white/50 hover:text-white/80 transition-colors flex items-center gap-1"
                    >
                      <ArrowLeft className="w-3.5 h-3.5" />
                      Utiliser le mot de passe
                    </button>
                  </div>
                )}

                {(!useCode || codeSent) && (
                  <Button
                    type={useCode ? "button" : "submit"}
                    onClick={useCode ? verifyLoginCode : undefined}
                    data-testid="auth-submit-btn"
                    disabled={loading || (useCode && loginCode.length !== 6)}
                    className="auth-cta group relative h-12 w-full overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 font-semibold text-white shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)] transition-all hover:brightness-110 active:scale-[0.99]"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : useCode ? "Vérifier le code" : (
                      mode === "login" ? "Se connecter" : "Commencer"
                    )}
                  </Button>
                )}
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-white/50">
                  {mode === "login" ? "Pas encore de compte ?" : "Déjà un compte ?"}
                  {" "}
                  <button
                    data-testid="auth-switch-mode-btn"
                    onClick={switchMode}
                    className="font-medium text-blue-400 transition-colors hover:text-blue-300"
                  >
                    {mode === "login" ? "S'inscrire" : "Se connecter"}
                  </button>
                </p>
                <p className="mx-auto mt-4 max-w-[340px] text-[11.5px] leading-relaxed text-white/30">
                  En continuant, vous acceptez les{" "}
                  <button type="button" onClick={() => { closeModal(); navigate("/conditions-utilisation"); }} className="underline decoration-white/20 underline-offset-2 hover:text-white/60">conditions d'utilisation</button>
                  {" "}et la{" "}
                  <button type="button" onClick={() => { closeModal(); navigate("/politique-confidentialite"); }} className="underline decoration-white/20 underline-offset-2 hover:text-white/60">politique de confidentialité</button>.
                </p>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
